import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from '@jotpsych/ui/mui';
import SentryService from '@services/SentryService';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { formatSnoozeDate } from '../components/v2/work/snoozePresets';
import {
  billingV2Api,
  useAddEncounterWorkNoteMutation,
  useEncounterWorkDispositionMutation,
  V2_TAGS,
} from '../store/v2-api';
import type {
  EncounterWorkAction,
  EncounterWorkPending,
  UseEncounterWorkDispositionArgs,
  UseEncounterWorkDispositionResult,
} from '../types/encounterWork';

const FAILURE_COPY: Record<EncounterWorkAction, string> = {
  archive: 'Could not archive. Nothing changed.',
  restore: 'Could not restore. Nothing changed.',
  snooze: 'Could not snooze. Nothing changed.',
};

const OUTCOME: Record<EncounterWorkAction, 'archived' | 'snoozed' | 'restored'> = {
  archive: 'archived',
  restore: 'restored',
  snooze: 'snoozed',
};

/**
 * The one failure with something specific to say. A 409 is not a fault: it
 * means a colleague moved this encounter between the read and the write, and
 * the copy has to say so, because "Could not archive" invites a second press
 * that would fail the same way.
 */
const VERSION_CONFLICT_COPY = 'This claim was updated by someone else. Nothing changed. Try again.';

function isVersionConflict(error: unknown): boolean {
  return Boolean(error && typeof error === 'object' && 'status' in error && error.status === 409);
}

/**
 * THE FIVE VERBS, as one hook (GDN-3380, contract section 7).
 *
 * Archive, Snooze, Add note, Restore, and the Undo that turns the first two
 * back into the third. Home, Claims and the workbench all call this; none of
 * them owns any part of the behaviour, which is the point. Before this, the
 * same two verbs existed as `useClaimAttention` under a wrapper, keyed by
 * CLAIM, writing a lossy blob no queue read back. Everything here is keyed by
 * ENCOUNTER, because a biller who says "not this morning" means the visit, not
 * one of the three claims that visit has produced.
 *
 * WHAT THE HOOK OWNS, so that no caller has to remember it:
 *
 * - The snackbar, including Undo. Undo is not a fourth disposition: archive
 *   and snooze both undo to `restore`, a verb the server already has, so
 *   there is nothing to roll back and no client-side shadow state to keep.
 * - The outcome report. `onOutcome` fires only after the server said yes, and
 *   says WHICH verb happened, so the queue advance rule (GDN-3322) can ask its
 *   own question rather than infer one from whichever button was pressed.
 * - Statsig, with the two allow-listed `_submitted` events and no `_failed`
 *   companion. Failures are not interactions; the server emits the outcome.
 * - The failure snackbar. A verb that did not happen has to say so, or the row
 *   stays put with no explanation and the biller presses again.
 *
 * WHAT IT DOES NOT OWN: the 09:00 instant (`snoozePresets.ts` explains why the
 * client sends a bare date), and the refetch, which is tag invalidation in
 * `v2-api.ts` so that a surface nobody is looking at still comes back right.
 */
export function useEncounterWorkDisposition({
  encounterId,
  claimId,
  source,
  onOutcome,
  subjectLabel,
  expectedVersion,
}: UseEncounterWorkDispositionArgs): UseEncounterWorkDispositionResult {
  const dispatch = useDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { billing } = useEventTracking();
  const [dispositionMutation] = useEncounterWorkDispositionMutation();
  const [addNoteMutation] = useAddEncounterWorkNoteMutation();
  const [pending, setPending] = useState<EncounterWorkPending>(null);

  /**
   * The server call the three dispositions share: mutation, tracking, the
   * outcome report, and the failure snackbar. It returns whether it worked so
   * each verb can add its own receipt. The success snackbars stay OUT of here
   * because Undo has to call `restore`, and a `dispose` that built the Undo
   * button itself would have to reach forward to a verb defined below it.
   */
  const dispose = useCallback(
    async (
      action: EncounterWorkAction,
      opts: { untilDate?: string; note?: string } = {},
    ): Promise<boolean> => {
      setPending(action);
      /**
       * THE TRY WRAPS THE SERVER CALL AND NOTHING ELSE.
       *
       * Tracking and `onOutcome` used to live inside it, and that made the page
       * lie: `onOutcome` is a consumer callback, the workbench's version calls
       * `refetch()`, and RTK's `refetch` THROWS synchronously once the query has
       * unmounted. Archive, walk away, press Undo in the eight seconds the
       * snackbar is up, and a restore the server had already committed came back
       * as "Could not restore. Nothing changed." A catch around a caller's
       * callback reports the caller's failure as the server's.
       */
      let landed = false;
      try {
        await dispositionMutation({
          body: {
            action,
            claim_id: claimId ?? null,
            ...(opts.untilDate ? { until_date: opts.untilDate } : {}),
            ...(opts.note ? { note: opts.note } : {}),
            ...(expectedVersion === undefined ? {} : { expected_version: expectedVersion }),
          },
          encounterId,
        }).unwrap();
        landed = true;
      } catch (error) {
        const conflict = isVersionConflict(error);
        /* A conflict is someone else's successful write, not this client
           misbehaving, so it does not go to Sentry. Everything else does:
           there is no `_failed` Statsig event to carry it (the suffix is
           dropped by the pipeline), so without this a systematic failure is
           invisible until a biller notices their queue is not shrinking. */
        if (!conflict) {
          SentryService.captureException(error, {
            level: 'error',
            tags: {
              component: 'useEncounterWorkDisposition',
              feature: 'billing',
              operation: action,
            },
            extra: { encounterId, source },
          });
        }
        enqueueSnackbar(conflict ? VERSION_CONFLICT_COPY : FAILURE_COPY[action], {
          variant: 'error',
        });
        /* Re-read on a conflict so the next press carries the version the
           server actually holds, and so the kebab reflects what the colleague
           did. Nothing else refetches on a failed write. */
        if (conflict) {
          dispatch(
            billingV2Api.util.invalidateTags([
              V2_TAGS.CLAIM_WORK,
              { type: V2_TAGS.ENCOUNTER_WORK_NOTES, id: encounterId },
            ]),
          );
        }
      } finally {
        setPending(null);
      }

      if (!landed) return false;
      billing.workDispositionSubmitted({ action, source });
      /* An archive-with-a-note writes one note row server-side, so it is one
         note event too. The disposition event is not enough on its own: it
         cannot say whether the biller explained themselves. */
      if (opts.note) billing.workNoteSubmitted({ source });
      onOutcome?.(OUTCOME[action]);
      return true;
    },
    [
      billing,
      claimId,
      dispatch,
      dispositionMutation,
      encounterId,
      enqueueSnackbar,
      expectedVersion,
      onOutcome,
      source,
    ],
  );

  const restore = useCallback(async () => {
    if (await dispose('restore')) {
      enqueueSnackbar('Restored.', { variant: 'success' });
    }
  }, [dispose, enqueueSnackbar]);

  /** The success snackbar for a verb that hid something: it carries Undo. */
  const notifyWithUndo = useCallback(
    (message: string) => {
      enqueueSnackbar(message, {
        action: (snackbarKey): ReactNode => (
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              closeSnackbar(snackbarKey);
              void restore();
            }}
            sx={{ textTransform: 'none' }}
          >
            Undo
          </Button>
        ),
        /* Longer than the app's default. Undo is only useful while it is on
           screen, and the row it would bring back has just left the list. */
        autoHideDuration: 8000,
        variant: 'success',
      });
    },
    [closeSnackbar, enqueueSnackbar, restore],
  );

  const archive = useCallback(
    async (opts?: { note?: string }) => {
      if (await dispose('archive', { note: opts?.note })) {
        notifyWithUndo(
          subjectLabel
            ? `Archived ${subjectLabel}. Hidden until the payer acts.`
            : 'Archived. Hidden until the payer acts.',
        );
      }
    },
    [dispose, notifyWithUndo, subjectLabel],
  );

  const snooze = useCallback(
    async (opts: { untilDate: string; note?: string }) => {
      if (await dispose('snooze', { note: opts.note, untilDate: opts.untilDate })) {
        notifyWithUndo(`Snoozed until ${formatSnoozeDate(opts.untilDate)}.`);
      }
    },
    [dispose, notifyWithUndo],
  );

  const addNote = useCallback(
    async (opts: { body: string }) => {
      setPending('note');
      let landed = false;
      try {
        await addNoteMutation({
          body: { body: opts.body, claim_id: claimId ?? null },
          encounterId,
        }).unwrap();
        landed = true;
      } catch (error) {
        SentryService.captureException(error, {
          level: 'error',
          tags: { component: 'useEncounterWorkDisposition', feature: 'billing', operation: 'note' },
          extra: { encounterId, source },
        });
        enqueueSnackbar('Could not add the note. Nothing changed.', { variant: 'error' });
      } finally {
        setPending(null);
      }
      /* Same rule as the dispositions: the caller's own failure is not the
         server's, so the receipt and the event sit outside the try. It
         RESOLVES either way rather than rejecting — the contract's signature is
         `Promise<void>` and every caller fires it with `void`, so a rejection
         here would surface as an unhandled promise rejection rather than as
         anything a biller could act on. The failure snackbar is the report. */
      if (!landed) return;
      billing.workNoteSubmitted({ source });
      enqueueSnackbar('Note added.', { variant: 'success' });
    },
    [addNoteMutation, billing, claimId, encounterId, enqueueSnackbar, source],
  );

  return useMemo(
    () => ({ addNote, archive, pending, restore, snooze }),
    [addNote, archive, pending, restore, snooze],
  );
}
